// DERIVADO DE: gestion-web-cliente@1a5ce2d39
//   src/app/auxiliares/servicios/http/{reportes-genericos,eventos-genericos,
//   comandos,notas,asignacions,log-evento-lora}.service.ts
// CAMBIOS: seis clases de 40 líneas cada una colapsadas en una base común;
//   `@Service()`, `HttpClient` directo, rutas relativas. Las RUTAS no se tocan.

import { Service, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import type {
  EstadoEvento,
  EstadoEventoTecnico,
  IAsignacion,
  IComando,
  IConfigDeseada,
  IDownlinkJob,
  IEventoGenerico,
  IListado,
  ILogEvento,
  INota,
  IQueryParam,
  IReporteBase,
  IResumenCortesEnergiaSemana,
  IResumenAplicacionPerfil,
  IResumenDatosBase,
  ITratamientoEvento,
  IUsuario,
  MapaValoresReporte,
} from 'modelos';

import { queryParams } from '@core/datos/query-params';
import { SIN_NOTIFICAR_ERROR, contexto } from '@core/http/contexto';

/**
 * Las entidades que el detalle de luminaria muestra en sus solapas.
 *
 * **Por qué viven en `compartido/` y no en el dominio de luminarias.** Reportes,
 * eventos, comandos, notas y asignaciones no son de luminarias: son de todo el
 * sistema. Si los colgara del dominio, el día que Red LoRaWAN o Puestas los
 * necesiten habría que importar de un dominio a otro, que es justo lo que
 * `eslint.config.mjs` prohíbe.
 *
 * **Y por qué no son un `Lista2Service`.** Acá cada entidad tiene su clase, con
 * su tipo, y nada más que su listado. Lo que mata `Lista2Service` (ADR-005) no
 * es que centralice HTTP: es que centraliza **60 entidades con caché y polling
 * compartido** en un archivo de 3.373 líneas del que después nadie puede sacar
 * nada.
 */
abstract class ListadoHttp<T> {
  protected readonly http = inject(HttpClient);
  protected abstract readonly ruta: string;

  /** `silencioso`: si falla, no se avisa al usuario (para los sondeos de fondo). */
  listar(query?: IQueryParam, opciones?: { silencioso?: boolean }): Promise<IListado<T>> {
    return firstValueFrom(
      this.http.get<IListado<T>>(this.ruta, {
        params: queryParams(query),
        context: opciones?.silencioso ? contexto(SIN_NOTIFICAR_ERROR) : undefined,
      }),
    );
  }

  porId(id: string): Promise<T> {
    return firstValueFrom(this.http.get<T>(`${this.ruta}/${id}`));
  }
}

@Service()
export class ReportesHttp extends ListadoHttp<IReporteBase<keyof MapaValoresReporte>> {
  protected readonly ruta = '/reportes-genericos';

  /**
   * Cortes de energía de la última semana, ya agregados por el backend.
   *
   * Es una vista calculada, no un listado: devuelve un día por posición con
   * cuántas luminarias se quedaron sin alimentación y cuáles. Calcularlo en el
   * navegador pidiendo los reportes crudos de 24.000 luminarias por siete días
   * no es una opción.
   */
  cortesPorSemana(query?: IQueryParam): Promise<IResumenCortesEnergiaSemana> {
    return firstValueFrom(
      this.http.get<IResumenCortesEnergiaSemana>(`${this.ruta}/cortes-energia-semana`, {
        params: queryParams(query),
      }),
    );
  }

  /** Borra un reporte. En IRIX sólo lo ofrece a nivel 0, y acá también. */
  eliminar(id: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.ruta}/${id}`));
  }
}

@Service()
export class EventosHttp extends ListadoHttp<IEventoGenerico> {
  protected readonly ruta = '/eventos-genericos';

  /**
   * Cambia el estado de **todos** los eventos que cumplen la consulta.
   *
   * Es lo que usa "finalizar el grupo": el grupo se manda como filtro y no como
   * lista de ids, porque el listado vivo tiene tope y un grupo grande puede no
   * haber entrado entero. **No crea tratamientos**: no queda quién ni por qué,
   * y por eso la pantalla lo avisa antes.
   */
  actualizarVarios(query: IQueryParam, dato: { estado: EstadoEvento }): Promise<unknown> {
    return firstValueFrom(this.http.post(`${this.ruta}/updateMany2`, { query, dato }));
  }
}

/**
 * Los tratamientos: cada paso que alguien registró sobre uno o varios eventos.
 *
 * El estado de un evento **no se edita**: se registra un tratamiento con el
 * estado nuevo y el backend lo aplica. Esa es la razón por la que hay historial
 * y no sólo un campo: quién lo tomó, quién lo liberó, quién lo finalizó y con
 * qué nota. Sirve igual para los eventos de guardia (`estado`) y para las
 * solicitudes de servicio técnico (`estadoTecnico`).
 *
 * Ojo con la ruta: es `tratamientoeventos`, todo junto, como la publica el
 * backend. Con guion da 404.
 */
@Service()
export class TratamientosHttp {
  private readonly http = inject(HttpClient);
  private readonly ruta = '/tratamientoeventos';

  listar(query?: IQueryParam): Promise<IListado<ITratamientoEvento>> {
    return firstValueFrom(
      this.http.get<IListado<ITratamientoEvento>>(this.ruta, { params: queryParams(query) }),
    );
  }

  registrar(cuerpo: {
    idsEventos: string[];
    estado?: EstadoEvento;
    estadoTecnico?: EstadoEventoTecnico;
    nota?: string;
    notaInterna?: string;
    /** Sólo con `estado: 'En Espera'`: hasta cuándo queda pospuesto. */
    esperaHasta?: string;
    imagenes?: string[];
  }): Promise<ITratamientoEvento> {
    return firstValueFrom(this.http.post<ITratamientoEvento>(`${this.ruta}/generico`, cuerpo));
  }
}

@Service()
export class ComandosHttp extends ListadoHttp<IComando> {
  protected readonly ruta = '/comandos';
}

@Service()
export class AsignacionesHttp extends ListadoHttp<IAsignacion> {
  // Sí, con mayúscula: así está publicada la ruta en el backend, y el backend
  // no se toca (ADR-008).
  protected readonly ruta = '/Asignacions';
}

@Service()
export class EventosLoraHttp extends ListadoHttp<ILogEvento> {
  protected readonly ruta = '/logEventoMqtts';

  /**
   * El **último** evento de cada nodo, uno por nodo.
   *
   * Sirve para responder "cómo está transmitiendo el parque ahora" sin traer
   * todos los uplinks: con 24.000 nodos, la diferencia es entre una consulta y
   * varios millones de documentos.
   */
  ultimos(query?: IQueryParam): Promise<ILogEvento[]> {
    return firstValueFrom(
      this.http.get<ILogEvento[]>(`${this.ruta}/lastLogEventos`, { params: queryParams(query) }),
    );
  }
}

/**
 * El downlink tal como lo vive **nuestra** fila de envío: cuándo se encoló,
 * cuándo se despachó, cuántos intentos y con qué veredicto.
 *
 * Es la mitad del par que cuenta la historia de un comando. La otra mitad es
 * `IComando`, que cuenta lo que pasó del lado del **dispositivo**
 * (recibido / ejecutado). Confundirlas es de donde salen las cronologías que
 * mienten: un downlink "Enviado" con el nodo que nunca lo aplicó.
 */
@Service()
export class DownlinkJobsHttp extends ListadoHttp<IDownlinkJob> {
  protected readonly ruta = '/downlinkjobs';
}

/**
 * Resúmenes precalculados por `gestion-cron`.
 *
 * Es lo que hace que un dashboard de consumo mensual sea una consulta y no una
 * agregación sobre millones de reportes en el momento de abrir la pantalla.
 */
@Service()
export class ResumenesHttp extends ListadoHttp<IResumenDatosBase<never>> {
  protected readonly ruta = '/resumenes-datos';
}

/** La configuración que el reconciliador quiere dejar en el nodo, y sus diffs. */
@Service()
export class ConfigsDeseadasHttp extends ListadoHttp<IConfigDeseada> {
  protected readonly ruta = '/configdeseadas';

  /**
   * El avance de cada acción que mandó perfiles (Seguimiento): el backend agrupa
   * las deseadas por la marca de la acción. Sin `desde`, los últimos 30 días.
   *
   * `includeChildren` suma las de los clientes hijos (si el permiso tiene
   * habilitado verlos, igual que en el listado de envíos).
   */
  aplicaciones(params?: {
    desde?: string;
    limit?: number;
    includeChildren?: boolean;
  }): Promise<IResumenAplicacionPerfil[]> {
    const p: Record<string, string> = {};
    if (params?.desde) p['desde'] = params.desde;
    if (params?.limit) p['limit'] = String(params.limit);
    if (params?.includeChildren) p['includeChildren'] = 'true';
    return firstValueFrom(
      this.http.get<IResumenAplicacionPerfil[]>(`${this.ruta}/aplicaciones`, { params: p }),
    );
  }
}

/**
 * Sólo para mostrar quién hizo algo. La administración de usuarios vive en su
 * dominio; esto es la lectura mínima que necesitan las pantallas de otros.
 */
@Service()
export class UsuariosLecturaHttp extends ListadoHttp<IUsuario> {
  protected readonly ruta = '/usuarios';
}

@Service()
export class NotasHttp extends ListadoHttp<INota> {
  protected readonly ruta = '/notas';

  // `ICreateNota`/`IUpdateNota` son `Omit<Partial<INota>, …>` sobre un tipo
  // inferido de Zod, y TypeScript se queda sin profundidad al instanciarlos
  // (TS2589). `Partial<INota>` dice lo mismo para lo que hace falta acá.
  crear(dato: Partial<INota>): Promise<INota> {
    return firstValueFrom(this.http.post<INota>(this.ruta, dato));
  }

  actualizar(id: string, dato: Partial<INota>): Promise<INota> {
    return firstValueFrom(this.http.put<INota>(`${this.ruta}/${id}`, dato));
  }

  eliminar(id: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.ruta}/${id}`));
  }
}
